import React, { LazyExoticComponent, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { User } from 'oidc-client';
import { Route } from 'react-router-dom';
import AuthService from '../services/AuthService';
import OverlaySpinner from '../shared/OverlaySpinner';
import TopMenu from '../shared/TopMenu';
import './private-route.css';

/** Components composition passed to route. */
type ComponentFactory =
  | (() => JSX.Element)
  | LazyExoticComponent<() => JSX.Element>;

/**
 * Renders the specified component on a route, ensuring the user has
 * authenticated using `AuthService`. Components within `PrivateRoute` can
 * assume that the `AuthService` singleton has been loaded successfully. If the
 * user is not logged in - i.e., the backend refresh token is invalid or
 * missing - then the user will be directed to the login page.
 */
const PrivateRoute = ({
  component,
  header,
  ...rest
}: {
  /** Component to render after validating the user's credentials. */
  component: ComponentFactory;
  /** Optional value that can be set to `false` to render the page without header. */
  header?: boolean;
  /** Optional value indicating whether the route match must be exact. */
  exact?: boolean;
  /** Routing path for which to display the component. */
  path: string;
}) => {
  /** Track any login error. */
  const [loginError, setLoginError] = useState<string | undefined>();
  /** Authenticated user, here as state to trigger redraw on login. */
  const [user, setUser] = useState<User | null>(null);

  /** Authenticate if necessary. */
  useEffect(() => {
    new Promise<void>((resolve, reject) => {
      // If a code is in the search params, need to exchange it first.
      if (!!new URLSearchParams(document.location.search).get('code')) {
        AuthService.login().then(resolve).catch(reject);
      } else {
        resolve();
      }
    })
      .then(() => AuthService.getUser())
      .then((authUser) => {
        if (authUser) {
          setUser(authUser);
        } else {
          return AuthService.login();
        }
      })
      .catch((reason: any) => setLoginError(`${reason}`));
  }, []);

  const renderFn = (Component: ComponentFactory) => (props: any) =>
    (
      <>
        {loginError ? (
          <div className="private-route-error">
            <Typography variant="body1">Unable to log in.</Typography>
            <Typography variant="body1">
              Please try reloading the page.
            </Typography>
            <Typography variant="caption">Reason: {loginError}</Typography>
          </div>
        ) : !user ? (
          <OverlaySpinner size={24} />
        ) : (
          <div className="private-route-root">
            {header !== false && (
              <header>
                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid item>
                    <TopMenu />
                  </Grid>
                  {AuthService.user && (
                    <Grid item>
                      {AuthService.user.profile.given_name}{' '}
                      {AuthService.user.profile.family_name}
                    </Grid>
                  )}
                  <Grid item>
                    <Button
                      color="primary"
                      onClick={() => AuthService.logout()}
                    >
                      Logout
                    </Button>
                  </Grid>
                </Grid>
              </header>
            )}
            <div className="private-route-content">
              <Component {...props} />
            </div>
          </div>
        )}
      </>
    );

  return <Route {...rest} render={renderFn(component)} />;
};

export default PrivateRoute;
